import { InfoOutlined, Logout, Settings } from '@mui/icons-material';
import MailIcon from '@mui/icons-material/Mail';
import { Avatar, Badge, Box, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';
import { useAtom } from 'jotai';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { permissionsAtom, userTypeAtom } from '../../atoms/atom';
import img from '../../images/icone.png';
import { IAppBarProps } from '../../interfaces/IAppBarProps';
import { LoginService } from '../../services/LoginService';
import { NotificationService } from '../../services/NotificationService';
import { NotificationDTO } from '../../types/NotificationDTO';
import { triggerBrowserNotification } from '../../utils/notification';
import { useUtils } from '../../utils/utilFunctions';
import DrawerPages from '../menu/Menu';
import "./AppBarStyle.scss";

const AppBar: FC<IAppBarProps> = (props: IAppBarProps) => {
	const [anchorAvatar, setAnchorAvatar] = useState<null | HTMLElement>(null);
	const [anchorNotification, setAnchorNotification] = useState<null | HTMLElement>(null);
	const [shaking, setShaking] = useState(false);
	const [notifications, setNotifications] = useState<NotificationDTO[]>([]);
	const [permissions, setPermissions] = useAtom(permissionsAtom);
	const [, setUserType] = useAtom(userTypeAtom);
	const allowedNotify = permissions.includes("NOTIFICATIONS");
	const openAvatar = Boolean(anchorAvatar);
	const openNotification = Boolean(anchorNotification);
	const token = localStorage.getItem("username");
	const service = new NotificationService();
	const navigate = useNavigate();
	const { logout } = useUtils();

	const handleClickAvatar = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorAvatar(event.currentTarget);
	};
	const handleCloseAvatar = () => {
		setAnchorAvatar(null);
	};
	const handleClickNotification = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorNotification(event.currentTarget);
		setShaking(false);
	};
	const handleCloseNotification = () => {
		setAnchorNotification(null);
	};
	const handleConfig = () => {
		navigate("/configuracoes");
	};

	const handleOpenNotification = (notification: NotificationDTO) => {
		setNotifications(notifications.filter(n => n.id !== notification.id));
		service.markAsRead(notification.id);
		navigate(notification.resource);
	};

	const loadNotifications = async () => {
		if (notifications?.length > 0) return;
		const response = await service.getNotifications();
		if (response) {
			setNotifications(response.data);
			setShaking(response.data?.length > 0);
			if (response.data?.length > 0 && response.data.some(n => n.resource === '/cadastro-funcionarios')) {
				await triggerBrowserNotification(
					"Novo funcionário cadastrado!",
					"Clique para avaliar os dados de admissão",
					() => navigate("/cadastro-funcionarios")
				);
			}
		}
	};

	useEffect(() => {
		if (!allowedNotify || notifications.length > 0) return;
		const interval = setInterval(() => {
			if (!notifications.length) {
				loadNotifications();
			}
		}, 60000);
		return () => {
			clearInterval(interval);
		};
	}, [allowedNotify, notifications]);

	useEffect(() => {
		const loadPermissions = async () => {
			const response = await new LoginService().getPermissions();
			if (response?.data) {
				setPermissions(response.data.modules);
				setUserType(response.data.userType);
				loadNotifications();
			}
		};
		if (!permissions.length) {
			loadPermissions();
		}
	}, []);

	return (
		<div className='app-bar'>
			<DrawerPages/>
			<div className="modulo">
				{props.modulo}
			</div>
			<div className="hello">
				Olá, {token}!
			</div>
			{allowedNotify
				? <Box>
					<Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
						<Tooltip title="Notificações">
							<IconButton
								onClick={handleClickNotification}
								size="small"
								aria-controls={openAvatar ? 'account-menu' : undefined}
								aria-haspopup="true"
								aria-expanded={openAvatar ? 'true' : undefined}
							>
								<Badge id='notification' data-shaking={shaking} badgeContent={notifications.length} color="error" variant="dot">
									<MailIcon sx={{fontSize: '4vh'}} />
								</Badge>
							</IconButton>
						</Tooltip>
					</Box>
					<Menu
						anchorEl={anchorNotification}
						id="notification-menu"
						open={openNotification}
						onClose={handleCloseNotification}
						onClick={handleCloseNotification}
						transformOrigin={{ horizontal: 'right', vertical: 'top' }}
						anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
					>
							{notifications.length ? (notifications.map((notification) => (
								<MenuItem key={notification.id} onClick={() => handleOpenNotification(notification)}>
									<ListItemIcon>
										<InfoOutlined fontSize="small" />
									</ListItemIcon>
									{notification.content}
								</MenuItem>
							))) : (
								<MenuItem disabled>
									<ListItemIcon>
										<InfoOutlined fontSize="small" />
									</ListItemIcon>
									Nenhuma notificação
								</MenuItem>
							)}
					</Menu>
				</Box>
				: <></>
			}
			<Box>
				<Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
					<Tooltip title="Configurações">
						<IconButton
							onClick={handleClickAvatar}
							size="small"
							sx={{ ml: 2 }}
							aria-controls={openAvatar ? 'account-menu' : undefined}
							aria-haspopup="true"
							aria-expanded={openAvatar ? 'true' : undefined}
						>
							<Avatar
								src={img}
								sx={{ width: "5vh", height: "5vh" }}
							></Avatar>
						</IconButton>
					</Tooltip>
				</Box>
				<Menu
					anchorEl={anchorAvatar}
					id="account-menu"
					open={openAvatar}
					onClose={handleCloseAvatar}
					onClick={handleCloseAvatar}
					slotProps={{
						paper: {
						 elevation: 0,
						 sx: {
							overflow: 'visible',
							filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
							mt: 1.5,
							'& .MuiAvatar-root': {
							 width: 32,
							 height: 32,
							 ml: -0.5,
							 mr: 1,
							},
							'&::before': {
							 content: '""',
							 display: 'block',
							 position: 'absolute',
							 top: 0,
							 right: 14,
							 width: 10,
							 height: 10,
							 bgcolor: 'background.paper',
							 transform: 'translateY(-50%) rotate(45deg)',
							 zIndex: 0,
							},
						 },
						},
					 }}
					transformOrigin={{ horizontal: 'right', vertical: 'top' }}
					anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				>
					{permissions.includes("SETTINGS")
						? <MenuItem onClick={handleConfig}>
							<ListItemIcon>
								<Settings fontSize="small" />
							</ListItemIcon>
							Configurações
						</MenuItem>
						: <></>
					}
					<MenuItem onClick={logout}>
						<ListItemIcon>
							<Logout fontSize="small" />
						</ListItemIcon>
						Sair
					</MenuItem>
				</Menu>
			</Box>
		</div>
	);
}

export default AppBar